/*
 * @Description: 验证库
 */

import { checkIfExistsValue } from '@/api/common'
import type { Ref } from 'vue'

//数据库表名
export enum AsyncValidateValue {
	task_name = 'task_info',
	package_name = 'task_package',
	contact_phone = 'data_learner',
	group_name = 'article_person_group',
}
export enum AsyncValidateMsg {
	task_name = '任务名称',
	package_name = '任务包名称',
	contact_phone = '联系电话',
	group_name = '分组名称',
}
// * 异步校验生成器
export type AsyncValidateValueType = keyof typeof AsyncValidateValue
export function asyncValidatorGenerator(columnName: AsyncValidateValueType, delay = 500, defaultVal?: Ref<string>) {
	return useDebounceFn((rule, value, callback) => {
		if ((!rule.required && !value.trim()) || (defaultVal && value === defaultVal.value)) {
			return callback()
		}
		checkIfExistsValue({ value, columnName, tableName: AsyncValidateValue[columnName] }).then(({ result }) => {
			if (result) {
				callback(new Error(`${AsyncValidateMsg[columnName]}已存在`))
			} else {
				callback()
			}
		})
	}, delay)
}

// * 同步校验生成器
export type Validator = (value: any) => boolean | string
export function getValidator(validator: Validator, errMsg: string | (() => string)) {
	return (rule: any, value: any, callback: (params?: any) => void) => {
		if (!rule.required && !value.trim()) {
			return callback()
		}
		if (validator(value)) {
			callback()
		} else {
			callback(new Error(typeof errMsg === 'function' ? errMsg() : errMsg))
		}
	}
}
// 手机号验证
export const validateMobile: Validator = value => /^[1][3,4,5,7,8,9][0-9]{9}$/.test(value)
export const mobileValidator = getValidator(validateMobile, '请输入正确的手机号码')

// 邮箱验证
export const validateEmail: Validator = value => /^[A-Za-z0-9]+([_.][A-Za-z0-9]+)*@([A-Za-z0-9-]+\.)+[A-Za-z]{2,6}$/.test(value)
export const emailValidator = getValidator(validateEmail, '请输入正确的邮箱地址')

// 密码验证
export const validatePassword: Validator = value => /^.{6,30}$/.test(value)
export const passwordValidator = getValidator(validatePassword, '密码长度为6-30位')

// 微信号验证
export const validateWeChat: Validator = value => /^[a-zA-Z][a-zA-Z\d_-]{5,19}$/.test(value)
export const weChatValidator = getValidator(validateWeChat, '请输入正确的微信号')

// 日期时间验证
export const validateDateTime: Validator = value => /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(value)

// 身份证验证
export const validateCard: Validator = value => /(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/.test(value)

// 验证年龄
export const validateAge: Validator = value => /^(([0-9]|[1-9][1-9]|1[0-7][0-9])(\\.[0-9]+)?|180)$/.test(value)

// 中文验证
export const validateEnName: Validator = value => /^[\u4e00-\u9fa5]{0,10}$/.test(value)

// 验证IP
export const validateIP: Validator = value => /^((2[0-4]\d|25[0-5]|[01]?\d\d?)\.){3}(2[0-4]\d|25[0-5]|[01]?\d\d?)$/.test(value)

// 验证端口  为正整数，值范围1-65535
export const validatePort: Validator = value => /(^[1-9]\d{0,3}$)|(^[1-5]\d{4}$)|(^6[0-4]\d{3}$)|(^65[0-4]\d{2}$)|(^655[0-2]\d$)|(^6553[0-5]$)/.test(value)

// 服务地址限定条件 英文、数字、:、/、.、@、_、#、*,长度为1-256
export const validateAddress: Validator = value => /^[A-Za-z0-9:/@._#*]{1,256}$/.test(value)

// 验证1到99999999正整数
export const validateRate: Validator = value => /^[1-9][0-9]{0,7}$/.test(value)

// 经度验证
export const validateLon: Validator = value => /^[-+]?(0(\.\d{1,10})?|([1-9](\d)?)(\.\d{1,10})?|1[0-7]\d{1}(\.\d{1,10})?|180\.0{1,10})$/.test(value)

// 纬度验证
export const validateLat: Validator = value => /^[-+]?((0|([1-8]\d?))(\.\d{1,10})?|90(\.0{1,10})?)$/.test(value)

// 正整数
export const validatePort100: Validator = value => /^[1-9]\d*$/.test(value)

// 非英文数字验证
export const validateNotEnglishAndNumbers: Validator = value => /^[^\d]*$|^[^a-zA-Z]*$|[^\da-zA-Z]/.test(value)

/**
 * 身份证校验
 */
const checkIdCard = {
	// Wi 加权因子 Xi 余数0~10对应的校验码 Pi省份代码
	Wi: [7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2],
	Xi: [1, 0, 'X', 9, 8, 7, 6, 5, 4, 3, 2],
	Pi: [11, 12, 13, 14, 15, 21, 22, 23, 31, 32, 33, 34, 35, 36, 37, 41, 42, 43, 44, 45, 46, 50, 51, 52, 53, 54, 61, 62, 63, 64, 65, 71, 81, 82, 91],
	message: '',

	// 检验18位身份证号码出生日期是否有效
	// parseFloat过滤前导零，年份必需大于等于1900且小于等于当前年份，用Date()对象判断日期是否有效。
	brithday18(sIdCard: string) {
		const year = parseFloat(sIdCard.substr(6, 4))
		const month = parseFloat(sIdCard.substr(10, 2))
		const day = parseFloat(sIdCard.substr(12, 2))
		const checkDay = new Date(year, month - 1, day)
		const nowDay = new Date()

		if (year >= 1900 && year <= nowDay.getFullYear() && month === checkDay.getMonth() + 1 && day === checkDay.getDate()) {
			return true
		}

		this.message += '18位身份证号码出生日期格式无效；'
	},

	// 检验15位身份证号码出生日期是否有效
	brithday15(sIdCard: string) {
		const year = parseFloat(sIdCard.substr(6, 2))
		const month = parseFloat(sIdCard.substr(8, 2))
		const day = parseFloat(sIdCard.substr(10, 2))
		const checkDay = new Date(year, month - 1, day)

		if (month === checkDay.getMonth() + 1 && day === checkDay.getDate()) {
			return true
		}

		this.message += '15位身份证号码出生日期格式无效；'
	},

	// 检验校验码是否有效
	validate(sIdCard: string) {
		const aIdCard = sIdCard.split('')
		let sum = 0
		for (let i = 0; i < this.Wi.length; i++) {
			sum += this.Wi[i] * (aIdCard[i] as any) // 线性加权求和
		}

		const index = sum % 11 // 求模，可能为0~10,可求对应的校验码是否于身份证的校验码匹配
		if (this.Xi[index] == aIdCard[17].toUpperCase()) {
			return true
		}

		this.message += '校验码无效；'
	},

	// 检验输入的省份编码是否有效
	province(sIdCard: string) {
		const p2 = Number(sIdCard.substr(0, 2))
		for (let i = 0; i < this.Pi.length; i++) {
			if (this.Pi[i] === p2) {
				return true
			}
		}

		this.message += '省份编码无效；'
	},

	// 身份证校验
	test(sIdCard: string) {
		this.message = ''

		// 去除字符串的前后空格，允许用户不小心输入前后空格
		// let sIdCard = sIdCard.replace(/^\s+|\s+$/g,"");

		// 判断是否全为18或15位数字，最后一位可以是大小写字母X
		if (!/^\d{17}[\dxX]$|^\d{15}$/.test(sIdCard)) {
			return '身份证号码须为18位或15位数字' // 允许用户输入大小写X代替罗马数字的Ⅹ
		} else if (sIdCard.length === 18) {
			if (this.province(sIdCard) && this.brithday18(sIdCard) && this.validate(sIdCard)) {
				return ''
			} else {
				return this.message
			}
		} else if (sIdCard.length === 15) {
			if (this.province(sIdCard) && this.brithday15(sIdCard)) {
				return ''
			} else {
				return this.message
			}
		}

		return ''
	},
}
export const validateIdCard: Validator = (value: string) => !checkIdCard.test(value)
export const idCardValidator = getValidator(validateIdCard, '请输入正确的身份证号码')
